/**
 * components/landing/ai-platform/sections/UseCases.jsx — §4.10.
 * Seven practical scenarios with the module they hit.
 */

import React from 'react'
import {
  MessageSquare, BookOpen, Rocket, GraduationCap, Wand2, PieChart, Cog, ArrowRight, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { USE_CASES } from '../data'
import { TONE } from '../_shared'

const ICONS = { MessageSquare, BookOpen, Rocket, GraduationCap, Wand2, PieChart, Cog }

export default function UseCases() {
  return (
    <section className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>AI Use Cases</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Real scenarios. <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">Real outcomes.</span></>}
            subtitle="Seven ways operators, analysts and admins use Kalki Ai Cloud’s AI every day. Each maps to a module you’ll actually use."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          {USE_CASES.map((u) => {
            const tone = TONE[u.tone] || TONE.orange
            const Icon = ICONS[u.iconKey] || Sparkles
            return (
              <div key={u.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl transition-all hover:border-white/20 hover:bg-white/[0.03] overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex items-start gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 19, height: 19 }} />
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-base font-semibold text-white leading-tight">{u.label}</h4>
                      <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[10.5px] font-mono text-zinc-400">
                        <ArrowRight className="h-2.5 w-2.5" /> {u.module}
                      </span>
                    </div>
                    <p className="text-[13.5px] text-zinc-300 leading-relaxed mt-2">{u.body}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
